// ---------------------------------------------------------------------------
// setupSql.js
//
// Loads the "one-paste" Supabase migration SQL into memory at page load time
// via Vite's `?raw` import. Two things need that text:
//
//  1. The SchemaSetupBanner's "Copy setup SQL" button, and
//  2. The /go-live page's Step 2 "Copy setup SQL" button.
//
// Why not just `fetch('/APPLY_ALL_MIGRATIONS.sql')` on click?
//
//   iOS Safari (and, less strictly, Firefox) only allow
//   `navigator.clipboard.writeText` when the write happens synchronously
//   inside the same task as the user gesture that triggered it. Awaiting
//   a network fetch first breaks that user-gesture chain, so the copy
//   silently fails with "The write permission was denied" — which is what
//   the field report was: a teacher tapping "Copy setup SQL", getting
//   "Couldn't copy," and giving up.
//
//   Importing the SQL at bundle time means the text is already sitting in
//   a module-level constant by the time the button is even rendered, so
//   the click handler can call writeText synchronously and iOS is happy.
//
// The file is ~67KB of plain SQL. Bundling it adds roughly one gzipped
// 20KB entry to the initial JS payload — cheap for the payoff (a critical
// copy button that actually works on the phone where 90% of our teachers
// are).
//
// The `?raw` suffix is a Vite convention that returns the file's contents
// as a string instead of a URL. Works with any extension.
// ---------------------------------------------------------------------------

import setupSqlText from '../../supabase/APPLY_ALL_MIGRATIONS.sql?raw';

export const SETUP_SQL = setupSqlText;

// Same-origin static asset URL, useful when a link/download is preferable
// to a clipboard copy (e.g. the "Download setup file" fallback and the
// "Open on GitHub" link).
export const SETUP_SQL_STATIC_URL = '/APPLY_ALL_MIGRATIONS.sql';

// Attempt to copy the setup SQL to the clipboard. MUST be called
// synchronously from inside a click/tap handler — do not `await` anything
// before calling this or iOS Safari will revoke the clipboard permission.
//
// Returns a Promise<boolean>: true on success, false on failure (permission
// denied, no clipboard API, execCommand rejected, etc.). Callers should
// present a visible fallback (Download button, inline <pre>) when this
// returns false.
export async function copySetupSql() {
  const text = SETUP_SQL;
  if (!text) return false;

  // Modern path — this is the one iOS Safari actually cares about.
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy path
    }
  }

  // Legacy path for older Safari / embedded webviews without the async
  // clipboard API. execCommand is deprecated but still supported in every
  // browser that lacks navigator.clipboard, and it does keep the copy on
  // the user gesture.
  if (typeof document === 'undefined') return false;
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
